import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { FileText, Mail, Lock, ArrowRight } from 'lucide-react';

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2.5 mb-12">
            <div className="w-10 h-10 bg-gradient-to-br from-[#FF6B4A] to-[#FBBF24] rounded-xl flex items-center justify-center shadow-sm">
              <FileText className="w-5 h-5 text-white" strokeWidth={2} />
            </div>
            <span className="text-2xl font-heading font-extrabold tracking-tight text-stone-900">Facturcal</span>
          </div>

          <h2 className="text-3xl font-heading font-extrabold tracking-tight text-stone-950 mb-2">
            Bienvenido de vuelta
          </h2>
          <p className="text-stone-500 mb-10">
            Accede a tu cuenta para gestionar tu facturación
          </p>

          {error && (
            <div className="bg-red-50 text-red-700 px-4 py-3 rounded-xl mb-6 text-sm border border-red-100">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-stone-700 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 rounded-xl bg-stone-50 border-transparent h-12 focus:bg-white focus:border-[#FF6B4A] focus:ring-2 focus:ring-[#FF6B4A]/20"
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-stone-700 font-medium">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 rounded-xl bg-stone-50 border-transparent h-12 focus:bg-white focus:border-[#FF6B4A] focus:ring-2 focus:ring-[#FF6B4A]/20"
                  required
                  data-testid="login-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-[#FF6B4A] hover:bg-[#E05A3E] rounded-full h-12 font-semibold text-base shadow-lg shadow-orange-200/50 hover:shadow-xl hover:shadow-orange-300/50 transition-all duration-300 hover:-translate-y-0.5"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? 'Iniciando sesión...' : (
                <span className="flex items-center gap-2">
                  Iniciar sesión
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </form>

          <p className="mt-10 text-center text-sm text-stone-400">
            Contacte a la administradora para obtener acceso
          </p>
        </div>
      </div>

      {/* Right side - Hero Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 bg-cover bg-center relative"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1770129481027-baafe0748e1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBicmlnaHQlMjBjcmVhdGl2ZSUyMG9mZmljZSUyMHdvcmtzcGFjZXxlbnwwfHx8fDE3Nzc4NDExMjV8MA&ixlib=rb-4.1.0&q=85)'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B4A]/20 to-[#FBBF24]/10" />
        <div className="relative z-10 flex flex-col justify-end p-16 text-white">
          <div className="bg-black/30 backdrop-blur-md rounded-3xl p-8 max-w-md">
            <h1 className="text-3xl font-heading font-extrabold tracking-tight mb-3">
              La facturación simplificada
            </h1>
            <p className="text-white/80 text-base leading-relaxed">
              Gestiona facturas, presupuestos y albaranes con un asistente de IA integrado.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
